import Swiper from 'swiper'
import SwiperCore, { Navigation } from 'swiper/core'
import classes from '~/config/classes'
SwiperCore.use([Navigation])

export default class CollectionTabSlider {
  constructor(component) {
    this.dom = {
      $component: component,
      $sectionId: component.dataset.sectionId,
      $sectionContainer: component.querySelector('.js-collection-tab-section'),
      $tabHeader: component.querySelector('.js-collection-tab-header'),
      $tabLink: component.querySelectorAll('.js-collection-tab-link'),
      $tabContainerHidden: window.theme.hiddenTabsliders[component.dataset.sectionId] || null,
    }
    this.state = {
      activeTabContainer: null,
      activeTabLink: null,
      tabContainerClass: '.js-collection-tab-container',
      tabLinkClass: '.js-collection-tab-link',
    }
    this.mount()
    this.bindToWindow()
  }

  mount() {
    this.initActiveContainer()
    this.initTabs()
    this.setHeaderHeight()
    this.initSlider()
  }

  /**
   * Sets the CSS variable for the tab header height.
   */
  setHeaderHeight() {
    const { dom } = this
    const height = dom.$tabHeader.offsetHeight || 0
    document.documentElement.style.setProperty(
      '--tab-header-height',
      `${height}px`,
    )
  }

  /**
   * Initializes the tab links.
   * @param {HTMLElements} tabLinks - tab links for the current tab container.
   */
  initTabs() {
    const { state, dom } = this

    dom.$tabLink.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault()
        const tabData = event.target.dataset.categoryTabLink
        if (state.activeTabContainer.dataset.categoryTabContainer === tabData) {
          return
        }

        state.activeTabLink = tabData
        this.updateTabLinkState(tabData)
        this.handleActiveContainer(tabData)
      })
    })
  }

  initActiveContainer() {
    const { state, dom } = this
    Array.from(dom.$tabLink).find((link) => {
      if (link.classList.contains(classes.active)) {
        const tabData = link.dataset.categoryTabLink
        this.handleActiveContainer(tabData)
        state.activeTabLink = tabData
        return true
      }

      return false
    })
  }

  /**
   * Initializes a hidden tab containers.
   * - this is called when a tab link is clicked for the first time and
   * the tab container is not yet initialized.
   * @param {String} data - category data from the tab link.
   **/
  initHiddenContainer(data) {
    const { dom } = this
    if (!dom.$tabContainerHidden) {
      return
    }

    const hiddenContainer = Array.from(dom.$tabContainerHidden).find((tabContainer) => {
      return tabContainer.handle === data
    })

    if (hiddenContainer.length === 0) {
      return
    }

    const parser = new DOMParser()
    const element = parser.parseFromString(hiddenContainer.html, 'text/html')

    dom.$sectionContainer.append(element.body.firstChild)

    this.initSlider()

    const mountCards = new Event('mountproductcards')
    document.dispatchEvent(mountCards)
  }

  /**
   * Handles tab container active state.
   * - initializes any hidden tab containers before toggling the active state.
   * @param {String} data
   **/
  handleActiveContainer(data) {
    const { dom, state } = this
    const tabContainers = Array.from(dom.$component.querySelectorAll(state.tabContainerClass))

    tabContainers.forEach((tabContainer) => {
      if (tabContainer.dataset.categoryTabContainer !== data) {
        tabContainer.classList.remove(classes.active)
      }
    })

    const activeTabContainer = tabContainers.find((container) => {
      return container.dataset.categoryTabContainer === data
    })

    if (!activeTabContainer) {
      this.initHiddenContainer(data)
      return
    }

    state.activeTabContainer = activeTabContainer

    const tabContainer = tabContainers.find((container) => {
      return container.dataset.categoryTabContainer === data
    })

    tabContainer.classList.add(classes.active)
  }

  /**
   * Updates the active state of the tab links.
   * - since the tab links are scoped inside the tab container,
   * we need update it for the active tab container.
   **/
  updateTabLinkState(data) {
    const { dom } = this

    const tabLinks = Array.from(dom.$tabLink)

    tabLinks.forEach((tabLink) => {
      tabLink.classList.remove(classes.active)
    })

    const activeTabLink = tabLinks.find((link) => {
      return link.dataset.categoryTabLink === data
    })

    activeTabLink.classList.add(classes.active)
  }

  /**
   * Initializes the Swiper slider for the active tab container.
   * - this is called once for each tab container.
   **/
  initSlider() {
    const { dom, state } = this
    const containers = dom.$component.querySelectorAll(state.tabContainerClass)
    const tabContainers = Array.from(containers)
    const activeContainer = tabContainers.find((container) => {
      return container.classList.contains(classes.active)
    })

    const sliderContainer = activeContainer.querySelector('.js-productSliderContainer')
    const prevEl = activeContainer.querySelector('.js-productSliderPrev')
    const nextEl = activeContainer.querySelector('.js-productSliderNext')

    new Swiper(sliderContainer, {
      slidesPerView: 'auto',
      navigation: {
        prevEl,
        nextEl,
      },
    })

    activeContainer.classList.add(classes.ready)
    state.activeTabContainer = activeContainer
  }

  bindToWindow() {
    window.theme = window.theme || {}
    window.theme.CollectionTabSlider = this
  }
}
